import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminItemNormativeExclusion } from "../../../../../http/patrol/types/itemNormativeExclusions";
import { AdminRoofSubstructure } from "../../../../../http/patrol/types/roofSubstructures";
import { useItemNormativeExclusionsApi } from "../../../../../http/patrol/useItemNormativesExclusion";
import { useRoofSubstructuresApi } from "../../../../../http/patrol/useRoofSubstructures";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";

interface ExcludedItemFormFormBody {
  item: SearchAdminItem | null;
  roofSubstructure: AdminRoofSubstructure | null;
}

interface ExcludedItemProps {
  normativeId: string;
  item?: AdminItemNormativeExclusion;
  isLoading?: boolean;
  onClose: () => void;
}

const ExcludedItemForm: FC<ExcludedItemProps> = ({ item, isLoading, normativeId, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const itemExclusionsApi = useItemNormativeExclusionsApi();
  const itemsApi = useItemsApi();
  const roofSubstructuresApi = useRoofSubstructuresApi();
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const roofSubstructuresResult = roofSubstructuresApi.useAdminRoofSubstructures({}, aborter.current.signal);

  return (
    <Form<ExcludedItemFormFormBody>
      submitText={i18n.translation.common.save}
      onSubmit={() => (formData) =>
        item?.id
          ? itemExclusionsApi
              .updateAdminExcludedItem(normativeId, item.id, {
                itemId: formData.item?.id!,
                roofSubstructureId: formData.roofSubstructure?.id,
              })
              .then(() => onClose())
              .catch((err) => dispatch(showErrorNotification(i18n, err)))
              .finally(() => setIsSubmitting(false))
          : itemExclusionsApi
              .insertAdminExcludedItem(normativeId, {
                itemId: formData.item?.id!,
                roofSubstructureId: formData.roofSubstructure?.id,
              })
              .then(() => onClose())
              .catch((err) => dispatch(showErrorNotification(i18n, err)))
              .finally(() => setIsSubmitting(false))
      }
      disabled={isSubmitting}
      secondaryButton={
        <Button
          buttonProps={{
            onClick: () => onClose(),
          }}
          type="secondary"
        >
          {i18n.translation.common.cancel}
        </Button>
      }
    >
      {({ control, setValue, reset }) => {
        useEffect(() => {
          item &&
            reset({
              item: item.item,
              roofSubstructure: item.roofSubstructure,
            });
        }, [item]);
        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                    // Fetch initial data on mount
                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultOptions}
                        loadOptions={loadOptions}
                        isClearable
                        isDisabled={isSubmitting}
                        getOptionLabel={(x) => x.code}
                        getOptionValue={(x) => x.id}
                        onChange={(value) => setValue("item", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="roofSubstructure"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.roofSubstructures.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={roofSubstructuresResult.data?.items ?? []}
                      isClearable
                      isDisabled={isSubmitting || !roofSubstructuresResult.data || isLoading}
                      isLoading={isSubmitting || !roofSubstructuresResult.data || isLoading}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      onChange={(value) => setValue("roofSubstructure", value ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default ExcludedItemForm;
